/**
 * Expansion handlers for pinned-* wrapper rows.
 *
 * The user's 2026-05-26 override turned pinned wrappers from non-expandable
 * shortcuts into full drill-in nodes. To keep them id-unique vs the canonical
 * Databases-drill-down (so `findNodeById`, React keys, and `selectedNodeId`
 * tracking don't collide), every descendant id rendered under a pinned wrapper
 * carries a `pinned-desc<->{wrapperId}<->{canonicalId}` prefix.
 *
 * Strategy: reuse the existing canonical loaders (`loadObjectFolder`,
 * `loadTableSubfolders`, etc.) but intercept their `updateNode` callback so
 * the children they emit get their ids rewritten before they land in the
 * tree. The loaders themselves remain pinning-agnostic.
 */

import type { QueryClient } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import type {
  DatabaseExplorerNode,
  FolderTreeNode,
  TablesFolderNode,
  ViewsFolderNode,
  StoredProceduresFolderNode,
  FunctionsFolderNode,
  ProgrammabilityFolderNode,
} from '../../explorer/models/DatabaseTreeModels';
import type {
  PinnedDatabaseNode,
  PinnedSchemaNode,
  PinnedFolderNode,
  PinnedObjectNode,
} from '../../explorer/models/nodeTypes';
import { buildSchemaContainersForDatabase } from '../../explorer/services/schemaGrouping';
import {
  createDatabaseObjectTypeFolders,
  createTableSubfolders,
  createColumnsFolderNode,
} from '../../explorer/models/DatabaseTreeModels';
import type { DatabaseEngineId } from '../../../core/engines/engineTypes';
import { isKustoProxyConnection } from '../../connections/services/kustoProxyConnection';
import {
  pinnedDescendantNodeId,
  isPinnedDescendantNodeId,
  pinnedRootIdFromDescendantId,
} from '../../explorer/utils/nodeIdUtils';
import { loadObjectFolder } from './objectFolderExpansion';
import { databaseObjectsQueryKey } from './databaseObjectsQueryKeys';
import type { DatabaseObject, DatabaseObjectType } from '../models/databaseObjectTypes';

/**
 * Recursively rewrite a subtree's ids + parentIds so every node lives under
 * the given pinned root. Skips the root call (`rootNode.id` is untouched —
 * the caller is the pinned wrapper itself, which already has the right id).
 */
export function rewriteSubtreeIds<T extends DatabaseExplorerNode>(
  pinnedRootId: string,
  node: T,
  newParentId: string,
): T {
  const newId = pinnedDescendantNodeId(pinnedRootId, node.id);
  const rewrittenChildren = (node.children ?? []).map((c) =>
    rewriteSubtreeIds(pinnedRootId, c as DatabaseExplorerNode, newId),
  );
  return {
    ...node,
    id: newId,
    parentId: newParentId,
    children: rewrittenChildren as T['children'],
  } as T;
}

/**
 * Synchronously populate a pinned-database with the standard object-type
 * folders. Each child folder lives under the pinned-database id and carries a
 * `pinned-desc<->...` id so it doesn't collide with the canonical drill-down.
 *
 * Idempotent — if children are already populated, this is a no-op.
 */
export function expandPinnedDatabase(args: {
  node: PinnedDatabaseNode;
  engine?: DatabaseEngineId;
  updateNode: (nodeId: string, updates: Partial<DatabaseExplorerNode>) => void;
}): void {
  const { node, engine, updateNode } = args;
  if (node.children && node.children.length > 0) return;

  const connectionId = node.metadata.connectionId;
  const database = node.metadata.name;
  if (!connectionId || !database) return;

  const folders = createDatabaseObjectTypeFolders(connectionId, database, engine, isKustoProxyConnection(connectionId));
  const pinnedFolders = folders.map((f) =>
    rewriteSubtreeIds(node.id, f as DatabaseExplorerNode, node.id),
  );
  updateNode(node.id, { children: pinnedFolders });
}

/**
 * Synchronously populate a pinned-schema container with the same three child
 * folders the canonical `schema-container-folder` renders (Tables / Views /
 * Programmability), each scoped to the pinned schema. Children carry
 * `pinned-desc<->...` ids so they don't collide with the canonical drill-down.
 *
 * If preloaded objects aren't available for this database, the schema
 * container falls back to empty folders that fetch on expand.
 */
export function expandPinnedSchema(args: {
  node: PinnedSchemaNode;
  getPreloadedObjects: (connectionId: string, database: string) => DatabaseObject[] | undefined;
  updateNode: (nodeId: string, updates: Partial<DatabaseExplorerNode>) => void;
}): void {
  const { node, updateNode, getPreloadedObjects } = args;
  if (node.children && node.children.length > 0) return;

  const md = node.metadata as {
    connectionId: string;
    database: string;
    schema: string;
    engine?: DatabaseEngineId;
  };
  if (!md.connectionId || !md.database || !md.schema) return;

  const preloaded = getPreloadedObjects(md.connectionId, md.database) ?? [];
  // Build the canonical schema-container shape for THIS schema only, then
  // pluck the schema-container's children (Tables / Views / Programmability).
  const containers = buildSchemaContainersForDatabase({
    objects: preloaded,
    connectionId: md.connectionId,
    database: md.database,
    engine: md.engine,
  });
  const container = containers.find((c) => c.metadata.schema === md.schema);
  const containerChildren = (container?.children ?? []) as DatabaseExplorerNode[];

  const pinnedChildren = containerChildren.map((c) =>
    rewriteSubtreeIds(node.id, c, node.id),
  );
  updateNode(node.id, { children: pinnedChildren });
}

/**
 * Asynchronously fetch + populate the object listing under a pinned-folder
 * (Tables / Views / Stored Procedures / Functions / Programmability). The
 * canonical `loadObjectFolder` is reused — we intercept its `updateNode`
 * callback to rewrite the emitted children's ids.
 *
 * For `programmability`, the pinned folder simply renders its two
 * sub-folders (Stored Procedures + Functions) synchronously.
 */
export async function expandPinnedFolder(args: {
  node: PinnedFolderNode;
  fetchObjectsForFolder: (
    objectType: DatabaseObjectType,
    connectionId: string,
    database: string,
  ) => Promise<DatabaseObject[]>;
  addDatabaseObjects: (connectionId: string, database: string, objects: DatabaseObject[]) => void;
  setLoadingObject: (connectionId: string, database: string, objectType: string, isLoading: boolean) => void;
  getPreloadedObjects: (connectionId: string, database: string) => DatabaseObject[] | undefined;
  updateNode: (nodeId: string, updates: Partial<DatabaseExplorerNode>) => void;
  onFetchError?: (connectionId: string, errorMessage: string) => void;
  /**
   * "The connect this expand was started for is over." Forwarded to
   * `loadObjectFolder`, which drops every write when it says stale — a pinned
   * folder is a tree node like any other and must not be filled from a dead
   * connect (nor grow an error tile from its cancelled query).
   */
  isStale?: () => boolean;
}): Promise<void> {
  const { node, updateNode } = args;
  const { connectionId, database } = node.metadata as { connectionId: string; database?: string };
  if (!connectionId || !database) return;

  // Programmability has no object listing — just render its two sub-folders.
  if (node.folderKind === 'programmability') {
    if (node.children && node.children.length > 0) return;
    const folders = createDatabaseObjectTypeFolders(
      connectionId,
      database,
      (node.metadata as { engine?: DatabaseEngineId }).engine,
    );
    const prog = folders.find((f) => f.type === 'programmability-folder') as
      | ProgrammabilityFolderNode
      | undefined;
    const progChildren = (prog?.children ?? []) as FolderTreeNode[];
    const pinnedChildren = progChildren.map((c) =>
      rewriteSubtreeIds(node.id, c as DatabaseExplorerNode, node.id),
    );
    updateNode(node.id, { children: pinnedChildren });
    return;
  }

  const objectType = FOLDER_TO_OBJECT_TYPE[node.folderKind];

  // Wrap updateNode so children's ids get rewritten before they hit the tree.
  const wrappedUpdateNode = (nodeId: string, updates: Partial<DatabaseExplorerNode>) => {
    if (Array.isArray(updates.children)) {
      const rewritten = (updates.children as DatabaseExplorerNode[]).map((c) =>
        rewriteSubtreeIds(node.id, c, node.id),
      );
      updateNode(nodeId, { ...updates, children: rewritten });
    } else {
      updateNode(nodeId, updates);
    }
  };

  // Build a minimal canonical-shaped folder node to feed loadObjectFolder.
  // The loader reads `metadata` (connectionId, database, schema) for its
  // fetch parameters; the pinned-folder metadata matches that shape today.
  const canonicalShape = node as unknown as
    | TablesFolderNode
    | ViewsFolderNode
    | StoredProceduresFolderNode
    | FunctionsFolderNode;

  await loadObjectFolder({
    node: canonicalShape,
    connectionId,
    database,
    objectType,
    fetchObjectsForFolder: args.fetchObjectsForFolder,
    addDatabaseObjects: args.addDatabaseObjects,
    setLoadingObject: args.setLoadingObject,
    getPreloadedObjects: args.getPreloadedObjects,
    updateNode: wrappedUpdateNode,
    onFetchError: args.onFetchError,
    ...(args.isStale ? { isStale: args.isStale } : {}),
  });
}

/**
 * Synchronously populate a pinned table or view with its sub-folders.
 * Tables: Columns / Keys / Constraints / Triggers / Indexes / Statistics.
 * Views:  Columns only (matches the canonical view-expansion shape).
 */
export function expandPinnedObject(args: {
  node: PinnedObjectNode;
  updateNode: (nodeId: string, updates: Partial<DatabaseExplorerNode>) => void;
}): void {
  const { node, updateNode } = args;
  if (node.children && node.children.length > 0) return;

  const md = node.metadata as { connectionId: string; database?: string; schema: string; name: string };
  if (!md.connectionId || !md.database) return;

  let subfolders: DatabaseExplorerNode[];
  if (node.objectType === 'table') {
    subfolders = createTableSubfolders(
      md.connectionId,
      md.database,
      md.schema,
      md.name,
      node.id,
    ) as unknown as DatabaseExplorerNode[];
  } else if (node.objectType === 'view') {
    // Views expose only Columns in the canonical tree — keep parity.
    const columns = createColumnsFolderNode(
      md.connectionId,
      md.database,
      md.schema,
      md.name,
      node.id,
    );
    subfolders = [columns as unknown as DatabaseExplorerNode];
  } else {
    return; // procs/funcs are leaves
  }

  // The subfolder factories already set parentId to `node.id` (the pinned
  // wrapper). Rewrite each subtree's ids so the sub-folder + everything
  // beneath it lives in the pinned-desc namespace.
  const pinnedSubfolders = subfolders.map((sf) =>
    rewriteSubtreeIds(node.id, sf, node.id),
  );
  updateNode(node.id, { children: pinnedSubfolders });
}

/**
 * Wrap an `updateNode` callback so that, when the node being expanded is itself
 * a pinned descendant (`pinned-desc<->...`), any children the canonical loaders
 * emit get their ids rewritten into the SAME pinned namespace before they land
 * in the tree.
 *
 * Review finding P1 — `useNodeExpansion` dispatches by `node.type`. A folder /
 * table that lives under a pinned wrapper keeps its canonical type
 * (`tables-folder`, `table`, `columns-folder`, ...), so it routes through the
 * canonical loader, which builds canonical child ids (`object<->...`,
 * `columns-folder<->...`). Those collide with the real Databases drill-down and
 * give the context menu a `pinned-desc` parent it can't parse. Routing the
 * loader's `updateNode` through this wrapper keeps the whole pinned subtree
 * id-namespaced at any depth, while the loaders stay pinning-agnostic.
 *
 * Returns the original `updateNode` unchanged when `expandingNodeId` is not a
 * pinned descendant (so canonical expansion is unaffected).
 */
export function wrapUpdateNodeForPinnedDescendant(
  expandingNodeId: string,
  updateNode: (nodeId: string, updates: Partial<DatabaseExplorerNode>) => void,
): (nodeId: string, updates: Partial<DatabaseExplorerNode>) => void {
  if (!isPinnedDescendantNodeId(expandingNodeId)) return updateNode;
  const pinnedRootId = pinnedRootIdFromDescendantId(expandingNodeId);
  if (!pinnedRootId) return updateNode;

  return (nodeId, updates) => {
    if (Array.isArray(updates.children)) {
      const rewritten = (updates.children as DatabaseExplorerNode[]).map((c) =>
        rewriteSubtreeIds(pinnedRootId, c, nodeId),
      );
      updateNode(nodeId, { ...updates, children: rewritten });
    } else {
      updateNode(nodeId, updates);
    }
  };
}

const FOLDER_TO_OBJECT_TYPE: Record<
  Exclude<PinnedFolderNode['folderKind'], 'programmability'>,
  DatabaseObjectType
> = {
  tables: 'table',
  views: 'view',
  storedProcedures: 'stored-procedure',
  functions: 'function',
  tableTypes: 'table-type',
  aliasTypes: 'alias-type',
  clrTypes: 'clr-type',
};

// Re-exports used by useNodeExpansion's tests + react-query glue. Kept
// inline so the consumer doesn't have to chase imports across files.
export { useQueryClient, type QueryClient, databaseObjectsQueryKey };
